/**
 * Reload-carried telemetry for the Builder page-tree fetch (PR #1120 review
 * round 1 — the second channel for triage C7).
 *
 * WHY A SECOND CHANNEL. `builder-page-view.tsx` beacons a terminal
 * page-tree fetch failure via `reportClientError` → POST
 * `/resources/client-error`. That endpoint sits in the SAME WAF path class as
 * the fetch that just failed: the production bot-score `managed_challenge`
 * rules exempt `/resources/*` only for `ip.src.country eq "US"`, so for the
 * primary failure cohort (non-US / geo-misresolved shopper whose page-tree XHR
 * was challenged) the beacon XHR is challenged identically and never reaches
 * Worker logs. The observability is ineffective exactly when it matters.
 *
 * A DOCUMENT navigation is different: a challenge on it is a solvable
 * interstitial, after which the browser re-issues the request with its
 * cookies. So the client plants a tiny first-party cookie right before the
 * guarded `location.reload()`, and the NEXT DOCUMENT REQUEST carries the
 * failure to the Worker, where `PageTreeReloadSignalMiddleware` logs one
 * structured warn and clears the cookie in the same response.
 *
 * CONSTRAINTS (load-bearing — do not relax):
 *  - SHORT-LIVED (Max-Age 60) and CLEARED ON FIRST READ. Any Cookie header at
 *    all makes a document request ineligible for the guest full-page HTML
 *    cache (`cloudflare/guest-html-cache.ts`: strictly cookieless) and varies
 *    the `Vary: Cookie` document response — a lingering cookie would silently
 *    demote the shopper out of the cache for its whole lifetime. 60s is only
 *    the backstop for a reload that never arrives.
 *  - PII-FREE: pathname + HTTP status + timestamp only. Never the query string
 *    (it can carry afid / email) and never customer data.
 *  - NEVER THROWS on either side — telemetry must not break the recovery it
 *    rides on, nor 500 a document request.
 *
 * Isomorphic on purpose: the client writes with `document.cookie`, the server
 * reads the `Cookie` header and builds the clearing `Set-Cookie`; both must
 * agree on the name, the wire format and the attributes (a delete only hits
 * the cookie when Path/Secure match).
 */
import { getCookieValue } from "~/utils/cookies";

export const PAGE_TREE_RELOAD_SIGNAL_COOKIE = "hec_ptf";
export const PAGE_TREE_RELOAD_SIGNAL_MAX_AGE_SECONDS = 60;
/** Log scope — distinct from the direct `builder-page-tree-fetch` beacon so
 *  the two channels stay separable (and summable) in Observability. */
export const PAGE_TREE_RELOAD_SIGNAL_SCOPE = "builder-page-tree-fetch-reload";
const MAX_PATH_LENGTH = 200;

export interface PageTreeReloadSignal {
  /** Builder page path the tree fetch failed for — pathname only. */
  path: string;
  /** HTTP status of the terminal non-OK response; null for a rejection. */
  status: number | null;
  /** Client wall-clock (ms) when the terminal failure was recorded. */
  ts: number;
}

/**
 * Wire format: `<encodeURIComponent(path)>|<status or empty>|<ts>`. Each part
 * is encoded separately so a `|` inside the path cannot desynchronise the
 * split; `|` itself is a legal cookie-octet.
 */
export function encodePageTreeReloadSignal(
  signal: PageTreeReloadSignal,
): string {
  return [
    encodeURIComponent(signal.path.slice(0, MAX_PATH_LENGTH)),
    signal.status === null ? "" : String(signal.status),
    String(signal.ts),
  ].join("|");
}

/** Strict decode — anything the encoder could not have produced is `null`. */
export function decodePageTreeReloadSignal(
  raw: string | null | undefined,
): PageTreeReloadSignal | null {
  if (!raw) return null;
  const parts = raw.split("|");
  if (parts.length !== 3) return null;
  let path: string;
  try {
    path = decodeURIComponent(parts[0]);
  } catch {
    return null;
  }
  if (!path.startsWith("/") || path.length > MAX_PATH_LENGTH) return null;
  const status = parts[1] === "" ? null : Number(parts[1]);
  if (
    status !== null &&
    !(Number.isInteger(status) && status >= 100 && status <= 999)
  ) {
    return null;
  }
  const ts = Number(parts[2]);
  if (!Number.isInteger(ts) || ts <= 0) return null;
  return { path, status, ts };
}

const attributes = (secure: boolean, maxAge: number): string =>
  `Path=/; SameSite=Lax; Max-Age=${maxAge}${secure ? "; Secure" : ""}`;

/** `document.cookie` string that plants the signal (client). */
export function buildPageTreeReloadSignalCookie(
  signal: PageTreeReloadSignal,
  secure: boolean,
): string {
  return `${PAGE_TREE_RELOAD_SIGNAL_COOKIE}=${encodePageTreeReloadSignal(signal)}; ${attributes(
    secure,
    PAGE_TREE_RELOAD_SIGNAL_MAX_AGE_SECONDS,
  )}`;
}

/** `Set-Cookie` header value that expires the signal (server). */
export function buildClearPageTreeReloadSignalCookie(secure: boolean): string {
  return `${PAGE_TREE_RELOAD_SIGNAL_COOKIE}=; ${attributes(secure, 0)}`;
}

/**
 * Client: plant the signal immediately before a `location.reload()`. Never
 * throws (`document.cookie` can throw in exotic embeds / blocked storage —
 * the reload itself must still happen).
 */
export function writePageTreeReloadSignal(signal: PageTreeReloadSignal): void {
  try {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return;
    }
    document.cookie = buildPageTreeReloadSignalCookie(
      signal,
      window.location.protocol === "https:",
    );
  } catch {
    // Telemetry must never break the recovery it rides on.
  }
}

export interface ConsumedPageTreeReloadSignal {
  /** Decoded signal, or `null` when the cookie was present but malformed. */
  signal: PageTreeReloadSignal | null;
  /** The `Set-Cookie` that expires the cookie — MUST go out on this response. */
  clearCookie: string;
}

/**
 * Server (pure): read the signal off the inbound `Cookie` header. `null` when
 * absent. When present — well-formed or not — the caller must append
 * `clearCookie` so the signal is consumed on first read.
 */
export function consumePageTreeReloadSignal(
  cookieHeader: string | null | undefined,
  secure: boolean,
): ConsumedPageTreeReloadSignal | null {
  const raw = getCookieValue(cookieHeader, PAGE_TREE_RELOAD_SIGNAL_COOKIE);
  if (raw === null) return null;
  return {
    signal: decodePageTreeReloadSignal(raw),
    clearCookie: buildClearPageTreeReloadSignalCookie(secure),
  };
}
